/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { AppLayout } from '@/components/layout/app-layout';
import { AuthLayout } from '@/components/layout/auth-layout';
import { ProtectedRoute } from '@/router/guards/protected-route';
import { PublicRoute } from '@/router/guards/public-route';
import { IS_AGENCY_MODULE_ENABLED } from '@/features/agency/feature-flag';
import { PRIVATE_ROUTES, PUBLIC_ROUTE } from './constant/routes';
import {
    preloadOpportunityDetailPage,
    preloadTicketDetailPage,
    preloadWhatsAppConnectPage,
} from './constant/route-helpers';

const LoginPage = lazy(() =>
    import('@/pages/auth/login-page').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
    import('@/pages/auth/register-page').then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
    import('@/pages/auth/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
    import('@/pages/auth/reset-password-page').then((m) => ({ default: m.ResetPasswordPage })),
);
const OAuthCallbackPage = lazy(() =>
    import('@/pages/auth/oauth-callback-page').then((m) => ({ default: m.OAuthCallbackPage })),
);
const DashboardPage = lazy(() =>
    import('@/pages/dashboard/dashboard-page').then((m) => ({ default: m.DashboardPage })),
);
/**
 * `/` → Today, CARRYING the query string and hash.
 *
 * A bare `<Navigate to={TODAY}>` drops them, and `/` is not only typed by
 * hand: older onboarding links deep-link at a wizard step (`/?leadStep=3`).
 * Today's `OnboardingGate` forwards that param on to the Set-up checklist
 * page, where the wizard now lives. Losing the param bounced the customer
 * straight back to the surface they had just clicked from.
 */
function HomeRedirect() {
    return <QueryPreservingRedirect to={PRIVATE_ROUTES.TODAY} />;
}

/**
 * The same carry, generalised: a path that MOVED redirects to its new home
 * with the old link's query and hash intact, plus whatever the new home needs
 * to mean the same thing (`/sequences` → `/automations?view=mine&kind=SEQUENCE`).
 *
 * `add` wins over the incoming query on purpose. A stale `?view=playbooks` on
 * an old `/sequences` bookmark must not land the reader somewhere that cannot
 * show sequences at all.
 */
function QueryPreservingRedirect({
    to,
    add,
    transform,
}: {
    readonly to: string;
    readonly add?: Readonly<Record<string, string>>;
    /** Rewrites params whose MEANING moved, not just their destination. */
    readonly transform?: (params: URLSearchParams) => void;
}) {
    const { search, hash } = useLocation();
    const params = new URLSearchParams(search);
    transform?.(params);
    for (const [key, value] of Object.entries(add ?? {})) params.set(key, value);
    const next = params.toString();
    return <Navigate to={{ pathname: to, search: next ? `?${next}` : '', hash }} replace />;
}

/**
 * The old Workflows list filtered on `status`; Automations filters on `state`,
 * which folds `pausedAt` in — so a bookmarked `?status=PUBLISHED` would be
 * silently ignored and quietly show the reader everything instead.
 */
const LEGACY_STATUS_TO_STATE: Readonly<Record<string, string>> = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
};

function normaliseLegacyWorkflowStatus(params: URLSearchParams): void {
    const status = params.get('status');
    params.delete('status');
    if (status === null || params.has('state')) return;
    const state = LEGACY_STATUS_TO_STATE[status];
    if (state !== undefined) params.set('state', state);
}

const TodayPage = lazy(() =>
    import('@/pages/today/today-page').then((m) => ({ default: m.TodayPage })),
);
const TodayLeadsPage = lazy(() =>
    import('@/pages/today/today-leads-page').then((m) => ({ default: m.TodayLeadsPage })),
);
const OutcomesPage = lazy(() =>
    import('@/pages/outcomes/outcomes-page').then((m) => ({ default: m.OutcomesPage })),
);
const OutcomesLeadsPage = lazy(() =>
    import('@/pages/outcomes/outcomes-leads-page').then((m) => ({
        default: m.OutcomesLeadsPage,
    })),
);
const GettingStartedRoute = lazy(() =>
    import('@/features/onboarding').then((m) => ({ default: m.GettingStartedRoute })),
);
const AgencyOverviewPage = lazy(() =>
    import('@/pages/agency/agency-overview-page').then((m) => ({
        default: m.AgencyOverviewPage,
    })),
);
const AgencyClientsPage = lazy(() =>
    import('@/pages/agency/agency-clients-page').then((m) => ({
        default: m.AgencyClientsPage,
    })),
);
const AddClientWizardPage = lazy(() =>
    import('@/pages/agency/add-client-wizard-page').then((m) => ({
        default: m.AddClientWizardPage,
    })),
);
const AgencyPricingPage = lazy(() =>
    import('@/pages/agency/agency-pricing-page').then((m) => ({
        default: m.AgencyPricingPage,
    })),
);
const AgencySetupsPage = lazy(() =>
    import('@/pages/agency/agency-setups-page').then((m) => ({
        default: m.AgencySetupsPage,
    })),
);
const AgencyBrandingPage = lazy(() =>
    import('@/pages/agency/agency-branding-page').then((m) => ({
        default: m.AgencyBrandingPage,
    })),
);
const AnalyticsOverviewPage = lazy(() =>
    import('@/pages/analytics/analytics-overview-page').then((m) => ({
        default: m.AnalyticsOverviewPage,
    })),
);
const AnalyticsMarketingPage = lazy(() =>
    import('@/pages/analytics/analytics-marketing-page').then((m) => ({
        default: m.AnalyticsMarketingPage,
    })),
);
const AnalyticsConversationsPage = lazy(() =>
    import('@/pages/analytics/analytics-conversations-page').then((m) => ({
        default: m.AnalyticsConversationsPage,
    })),
);
const AnalyticsRevenuePage = lazy(() =>
    import('@/pages/analytics/analytics-revenue-page').then((m) => ({
        default: m.AnalyticsRevenuePage,
    })),
);
const AnalyticsAppointmentsPage = lazy(() =>
    import('@/pages/analytics/analytics-appointments-page').then((m) => ({
        default: m.AnalyticsAppointmentsPage,
    })),
);
const AnalyticsDeliverabilityPage = lazy(() =>
    import('@/pages/analytics/analytics-deliverability-page').then((m) => ({
        default: m.AnalyticsDeliverabilityPage,
    })),
);
const CampaignsPage = lazy(() =>
    import('@/pages/campaigns/campaigns-page').then((m) => ({ default: m.CampaignsPage })),
);
const CreateCampaignPage = lazy(() =>
    import('@/pages/create-campaign-page').then((m) => ({ default: m.CreateCampaignPage })),
);
const EditCampaignPage = lazy(() =>
    import('@/pages/create-campaign-page').then((m) => ({ default: m.EditCampaignPage })),
);
const CampaignDesignPage = lazy(() =>
    import('@/pages/campaign-design-page').then((m) => ({ default: m.CampaignDesignPage })),
);
const CampaignDetailPage = lazy(() =>
    import('@/pages/campaign-detail-page').then((m) => ({ default: m.CampaignDetailPage })),
);
const ContactsPage = lazy(() =>
    import('@/pages/contacts/contacts-page').then((m) => ({ default: m.ContactsPage })),
);
const ImportContactsPage = lazy(() =>
    import('@/pages/contacts/import-contacts-page').then((m) => ({
        default: m.ImportContactsPage,
    })),
);
const ContactDetailPage = lazy(() =>
    import('@/pages/contacts/contact-detail-page').then((m) => ({ default: m.ContactDetailPage })),
);
const InboxPage = lazy(() =>
    import('@/pages/inbox/inbox-page').then((m) => ({ default: m.InboxPage })),
);
const DesignSystemPage = lazy(() =>
    import('@/pages/design-system/DesignSystemPage').then((m) => ({
        default: m.DesignSystemPage,
    })),
);
const ProfilePage = lazy(() =>
    import('@/pages/profile/profile-page').then((m) => ({ default: m.ProfilePage })),
);
const SegmentsPage = lazy(() =>
    import('@/pages/segments/segments-page').then((m) => ({ default: m.SegmentsPage })),
);
const SegmentCreatePage = lazy(() =>
    import('@/pages/segments/segment-create-page').then((m) => ({ default: m.SegmentCreatePage })),
);
const SegmentDetailPage = lazy(() =>
    import('@/pages/segments/segment-detail-page').then((m) => ({ default: m.SegmentDetailPage })),
);
const SettingsLayout = lazy(() =>
    import('@/components/layout/settings-layout').then((m) => ({ default: m.SettingsLayout })),
);
const SettingsChannelsPage = lazy(() =>
    import('@/pages/channels/settings-channels-page').then((m) => ({
        default: m.SettingsChannelsPage,
    })),
);
const ChannelDetailPage = lazy(() =>
    import('@/pages/channels/channel-detail-page').then((m) => ({ default: m.ChannelDetailPage })),
);
const ChannelProviderPage = lazy(() =>
    import('@/pages/channels/channel-provider-page').then((m) => ({
        default: m.ChannelProviderPage,
    })),
);
const ChannelsHubPage = lazy(() =>
    import('@/pages/channels/channels-hub-page').then((m) => ({ default: m.ChannelsHubPage })),
);
const AiSetupPage = lazy(() =>
    import('@/pages/ai-setup/ai-setup-page').then((m) => ({ default: m.AiSetupPage })),
);
const ChannelRedirect = lazy(() =>
    import('@/pages/channels/channel-redirect').then((m) => ({ default: m.ChannelRedirect })),
);
const NotFoundPage = lazy(() =>
    import('@/pages/not-found-page').then((m) => ({ default: m.NotFoundPage })),
);
const JoinLinksPage = lazy(() =>
    import('@/pages/join-links/join-links-page').then((m) => ({ default: m.JoinLinksPage })),
);
const JoinLinkFormPage = lazy(() =>
    import('@/pages/join-links/join-link-form-page').then((m) => ({
        default: m.JoinLinkFormPage,
    })),
);
const JoinLinkDetailPage = lazy(() =>
    import('@/pages/join-links/join-link-detail-page').then((m) => ({
        default: m.JoinLinkDetailPage,
    })),
);
const PublicJoinPageRoute = lazy(() =>
    import('@/pages/join-links/public/public-join-page').then((m) => ({
        default: m.PublicJoinPageRoute,
    })),
);
const LeadFormsPage = lazy(() =>
    import('@/pages/lead-forms/lead-forms-page').then((m) => ({ default: m.LeadFormsPage })),
);
const LeadFormWizardPage = lazy(() =>
    import('@/pages/lead-forms/lead-form-wizard-page').then((m) => ({
        default: m.LeadFormWizardPage,
    })),
);
const LeadFormTemplatesPage = lazy(() =>
    import('@/pages/lead-forms/lead-form-templates-page').then((m) => ({
        default: m.LeadFormTemplatesPage,
    })),
);
const LeadFormDetailPage = lazy(() =>
    import('@/pages/lead-forms/lead-form-detail-page').then((m) => ({
        default: m.LeadFormDetailPage,
    })),
);
const PublicLeadFormPageRoute = lazy(() =>
    import('@/pages/lead-forms/public/public-lead-form-page').then((m) => ({
        default: m.PublicLeadFormPageRoute,
    })),
);
const LeadFormPreviewPage = lazy(() =>
    import('@/pages/lead-forms/lead-form-preview-page').then((m) => ({
        default: m.LeadFormPreviewPage,
    })),
);
const ShortLinkDetailPage = lazy(() =>
    import('@/pages/short-links/short-link-detail-page').then((m) => ({
        default: m.ShortLinkDetailPage,
    })),
);
const JoinKeywordsPage = lazy(() =>
    import('@/pages/join-keywords/join-keywords-page').then((m) => ({
        default: m.JoinKeywordsPage,
    })),
);
const JoinKeywordFormPage = lazy(() =>
    import('@/pages/join-keywords/join-keyword-form-page').then((m) => ({
        default: m.JoinKeywordFormPage,
    })),
);
const JoinKeywordDetailPage = lazy(() =>
    import('@/pages/join-keywords/join-keyword-detail-page').then((m) => ({
        default: m.JoinKeywordDetailPage,
    })),
);
const LinkExpiredPage = lazy(() =>
    import('@/pages/link-expired/link-expired-page').then((m) => ({
        default: m.LinkExpiredPage,
    })),
);
const ShortLinkRedirectPage = lazy(() =>
    import('@/pages/short-links/short-link-redirect-page').then((m) => ({
        default: m.ShortLinkRedirectPage,
    })),
);
const PollsPage = lazy(() =>
    import('@/pages/polls/polls-page').then((m) => ({ default: m.PollsPage })),
);
const CreatePollPage = lazy(() =>
    import('@/pages/polls/create-poll-page').then((m) => ({ default: m.CreatePollPage })),
);
const PollDetailPage = lazy(() =>
    import('@/pages/polls/poll-detail-page').then((m) => ({ default: m.PollDetailPage })),
);
const PollAnalyticsPage = lazy(() =>
    import('@/pages/polls/poll-analytics-page').then((m) => ({ default: m.PollAnalyticsPage })),
);
const ModuleRecordsPage = lazy(() =>
    import('@/pages/module-records/module-records-page').then((m) => ({
        default: m.ModuleRecordsPage,
    })),
);
const ModuleRecordDetailPage = lazy(() =>
    import('@/pages/module-records/module-record-detail-page').then((m) => ({
        default: m.ModuleRecordDetailPage,
    })),
);
const OrganizationSettingsPage = lazy(() =>
    import('@/pages/organization/organization-settings-page').then((m) => ({
        default: m.OrganizationSettingsPage,
    })),
);
const TemplatesPage = lazy(() =>
    import('@/pages/templates/templates-page').then((m) => ({
        default: m.TemplatesPage,
    })),
);
const WhatsAppTemplateEditorPage = lazy(() =>
    import('@/pages/templates/whatsapp-template-editor-page').then((m) => ({
        default: m.WhatsAppTemplateEditorPage,
    })),
);
const TemplatesSmsTab = lazy(() =>
    import('@/pages/templates/templates-sms-tab').then((m) => ({
        default: m.TemplatesSmsTab,
    })),
);
const TemplatesEmailTab = lazy(() =>
    import('@/pages/templates/templates-email-tab').then((m) => ({
        default: m.TemplatesEmailTab,
    })),
);
const TemplatesWhatsAppTab = lazy(() =>
    import('@/pages/templates/templates-whatsapp-tab').then((m) => ({
        default: m.TemplatesWhatsAppTab,
    })),
);
const TemplatesLibraryTab = lazy(() =>
    import('@/pages/templates/templates-library-tab').then((m) => ({
        default: m.TemplatesLibraryTab,
    })),
);
const SmsTemplatesRedirect = lazy(() =>
    import('@/pages/templates/sms-templates-redirect').then((m) => ({
        default: m.SmsTemplatesRedirect,
    })),
);
const TemplatesIndexRedirect = lazy(() =>
    import('@/pages/templates/templates-index-redirect').then((m) => ({
        default: m.TemplatesIndexRedirect,
    })),
);
const EmailTemplateBuilderPage = lazy(() =>
    import('@/pages/email-templates/email-template-builder-page').then((m) => ({
        default: m.EmailTemplateBuilderPage,
    })),
);
const LegacyRedirect = lazy(() =>
    import('@/components/shared/legacy-redirect').then((m) => ({
        default: m.LegacyRedirect,
    })),
);
const EmailDomainsPage = lazy(() =>
    import('@/pages/settings/email-domains-page').then((m) => ({
        default: m.EmailDomainsPage,
    })),
);
const EmailSendersPage = lazy(() =>
    import('@/pages/settings/email-senders-page').then((m) => ({
        default: m.EmailSendersPage,
    })),
);
const WhatsAppSettingsPage = lazy(() =>
    import('@/pages/settings/whatsapp-settings-page').then((m) => ({
        default: m.WhatsAppSettingsPage,
    })),
);
const WhatsAppConnectPage = lazy(() =>
    preloadWhatsAppConnectPage().then((m) => ({ default: m.WhatsAppConnectPage })),
);
const OptOutSettingsPage = lazy(() =>
    import('@/pages/organization/opt-out-settings-page').then((m) => ({
        default: m.OptOutSettingsPage,
    })),
);
const UsersPage = lazy(() =>
    import('@/pages/settings/users/users-page').then((m) => ({ default: m.UsersPage })),
);
const RolesPage = lazy(() =>
    import('@/pages/settings/roles/roles-page').then((m) => ({ default: m.RolesPage })),
);
const AcceptInvitationPage = lazy(() =>
    import('@/pages/auth/accept-invitation-page').then((m) => ({
        default: m.AcceptInvitationPage,
    })),
);
const NoWorkspacePage = lazy(() =>
    import('@/pages/no-workspace/no-workspace-page').then((m) => ({
        default: m.NoWorkspacePage,
    })),
);
const ApiKeysSettingsPage = lazy(() =>
    import('@/features/api-keys').then((m) => ({
        default: m.ApiKeysSettingsPage,
    })),
);
const WebhooksSettingsPage = lazy(() =>
    import('@/features/webhooks').then((m) => ({
        default: m.WebhooksSettingsPage,
    })),
);
const WebhookFormPage = lazy(() =>
    import('@/features/webhooks').then((m) => ({
        default: m.WebhookFormPage,
    })),
);
const KnowledgeBasesPage = lazy(() =>
    import('@/features/knowledge-base').then((m) => ({
        default: m.KnowledgeBasesPage,
    })),
);
const ChatWidgetSettingsPage = lazy(() =>
    import('@/features/chat-widget').then((m) => ({
        default: m.ChatWidgetSettingsPage,
    })),
);
const KnowledgeBaseDetailPage = lazy(() =>
    import('@/features/knowledge-base').then((m) => ({
        default: m.KnowledgeBaseDetailPage,
    })),
);
const AiLeadResponderPage = lazy(() =>
    import('@/pages/ai-lead-responder/ai-lead-responder-page').then((m) => ({
        default: m.AiLeadResponderPage,
    })),
);
const EmployeesListPage = lazy(() =>
    import('@/features/ai-employees').then((m) => ({
        default: m.EmployeesListPage,
    })),
);
const EmployeeWizardPage = lazy(() =>
    import('@/features/ai-employees').then((m) => ({
        default: m.EmployeeWizardPage,
    })),
);
const EmployeeProfilePage = lazy(() =>
    import('@/features/ai-employees').then((m) => ({
        default: m.EmployeeProfilePage,
    })),
);
const AutomationsPage = lazy(() =>
    import('@/pages/automations/automations-page').then((m) => ({ default: m.AutomationsPage })),
);
const NewAutomationPage = lazy(() =>
    import('@/pages/automations/new-automation-page').then((m) => ({
        default: m.NewAutomationPage,
    })),
);
const WorkflowBuilderPage = lazy(() =>
    import('@/pages/workflows/workflow-builder-page').then((m) => ({
        default: m.WorkflowBuilderPage,
    })),
);
const WorkflowRunsPage = lazy(() =>
    import('@/pages/workflows/workflow-runs-page').then((m) => ({
        default: m.WorkflowRunsPage,
    })),
);
const WorkflowAnalyticsPage = lazy(() =>
    import('@/pages/workflows/workflow-analytics-page').then((m) => ({
        default: m.WorkflowAnalyticsPage,
    })),
);
const TagsSettingsPage = lazy(() =>
    import('@/features/tags').then((m) => ({
        default: m.TagsSettingsPage,
    })),
);
const CalendarPage = lazy(() =>
    import('@/pages/calendar/calendar-page').then((m) => ({ default: m.CalendarPage })),
);
const MeetingTypesPage = lazy(() =>
    import('@/pages/meeting-types/meeting-types-page').then((m) => ({
        default: m.MeetingTypesPage,
    })),
);
const RoutingFormsPage = lazy(() =>
    import('@/pages/routing-forms/routing-forms-page').then((m) => ({
        default: m.RoutingFormsPage,
    })),
);
const RoutingFormBuilderPage = lazy(() =>
    import('@/pages/routing-forms/routing-form-builder-page').then((m) => ({
        default: m.RoutingFormBuilderPage,
    })),
);
const AppointmentOutcomesPage = lazy(() =>
    import('@/pages/outcome-sets/appointment-outcomes-page').then((m) => ({
        default: m.AppointmentOutcomesPage,
    })),
);
const MeetingTypeFormPage = lazy(() =>
    import('@/pages/meeting-types/meeting-type-form-page').then((m) => ({
        default: m.MeetingTypeFormPage,
    })),
);
const AvailabilityPage = lazy(() =>
    import('@/pages/availability/availability-page').then((m) => ({
        default: m.AvailabilityPage,
    })),
);
const BookingsPage = lazy(() =>
    import('@/pages/bookings/bookings-page').then((m) => ({ default: m.BookingsPage })),
);
const CalendarsSettingsPage = lazy(() =>
    import('@/pages/settings/calendars-page').then((m) => ({
        default: m.CalendarsSettingsPage,
    })),
);
const FieldServiceSettingsPageRoute = lazy(() =>
    import('@/pages/settings/field-service-page').then((m) => ({
        default: m.FieldServiceSettingsPageRoute,
    })),
);
// Phase 10: the phone is an AI employee's channel. The old Settings → AI
// Phone Answering addresses redirect (pages/settings/ai-phone-*.tsx go in P10-11).
const AiPhoneRedirect = lazy(() =>
    import('@/features/voice-agent/components/ai-phone-redirect').then((m) => ({
        default: m.AiPhoneRedirect,
    })),
);
const AiPhoneCallsRedirect = lazy(() =>
    import('@/features/voice-agent/components/ai-phone-redirect').then((m) => ({
        default: m.AiPhoneCallsRedirect,
    })),
);
const PublicBookingPageRoute = lazy(() =>
    import('@/pages/book/public-booking-page').then((m) => ({
        default: m.PublicBookingPageRoute,
    })),
);
const ManageBookingPageRoute = lazy(() =>
    import('@/pages/book/manage-booking-page').then((m) => ({
        default: m.ManageBookingPageRoute,
    })),
);
const PublicRoutingFormPageRoute = lazy(() =>
    import('@/pages/book/public-routing-form-page').then((m) => ({
        default: m.PublicRoutingFormPageRoute,
    })),
);
const BillingPage = lazy(() =>
    import('@/features/billing').then((m) => ({ default: m.BillingPage })),
);
const ModuleSetupPage = lazy(() =>
    import('@/features/module-builder').then((m) => ({
        default: m.ModuleSetupPage,
    })),
);
const TicketsPage = lazy(() =>
    import('@/pages/tickets/tickets-page').then((m) => ({ default: m.TicketsPage })),
);
// Sourced from `preload.ts` so the route and its on-intent preloader name the
// same specifier — Vite resolves both to one chunk, and a rename cannot leave
// the preloader warming a chunk the router no longer uses.
const TicketDetailPage = lazy(() =>
    preloadTicketDetailPage().then((m) => ({ default: m.TicketDetailPage })),
);
const OpportunitiesPage = lazy(() =>
    import('@/pages/opportunities/opportunities-page').then((m) => ({
        default: m.OpportunitiesPage,
    })),
);
const OpportunityDetailPage = lazy(() =>
    preloadOpportunityDetailPage().then((m) => ({ default: m.OpportunityDetailPage })),
);
const OpportunityReportsPage = lazy(() =>
    import('@/pages/opportunities/opportunity-reports-page').then((m) => ({
        default: m.OpportunityReportsPage,
    })),
);
const SequenceWizardPage = lazy(() =>
    import('@/pages/sequences/sequence-wizard-page').then((m) => ({
        default: m.SequenceWizardPage,
    })),
);
const SequenceEditorPage = lazy(() =>
    import('@/pages/sequences/sequence-editor-page').then((m) => ({
        default: m.SequenceEditorPage,
    })),
);
const SequenceDesignPage = lazy(() =>
    import('@/pages/sequences/sequence-design-page').then((m) => ({
        default: m.SequenceDesignPage,
    })),
);
const SequenceAnalyticsPage = lazy(() =>
    import('@/pages/sequences/sequence-analytics-page').then((m) => ({
        default: m.SequenceAnalyticsPage,
    })),
);
const AdvertisingGooglePage = lazy(() =>
    import('@/pages/advertising/advertising-google-page').then((m) => ({
        default: m.AdvertisingGooglePage,
    })),
);
const AdvertisingMetaPage = lazy(() =>
    import('@/pages/advertising/advertising-meta-page').then((m) => ({
        default: m.AdvertisingMetaPage,
    })),
);
const AdvertisingConnectedAccountsPage = lazy(() =>
    import('@/pages/advertising/connected-accounts-page').then((m) => ({
        default: m.ConnectedAccountsPage,
    })),
);
const AdvertisingConnectAccountPage = lazy(() =>
    import('@/pages/advertising/connect-account-page').then((m) => ({
        default: m.ConnectAccountPage,
    })),
);
const AdvertisingCampaignPerformancePage = lazy(() =>
    import('@/pages/advertising/campaign-performance-page').then((m) => ({
        default: m.CampaignPerformancePage,
    })),
);
const CallsPage = lazy(() => import('@/features/calls').then((m) => ({ default: m.CallsPage })));
const VoiceSettingsPage = lazy(() =>
    import('@/features/voice').then((m) => ({ default: m.VoiceSettingsPage })),
);
const VoiceNumberSettingsPage = lazy(() =>
    import('@/features/voice').then((m) => ({ default: m.VoiceNumberSettingsPage })),
);

/**
 * Legacy `/ai-agents/:agentId` deep-link redirect → `/ai-employees/:employeeId`,
 * preserving the id (mirrors `ChannelRedirect`'s param-preserving pattern).
 * The old agent-builder feature module was removed entirely in Task 18; this
 * redirect keeps old bookmarks/links working against the renamed
 * `ai-employees` route.
 */
function AiAgentDetailRedirect() {
    const { agentId } = useParams<{ agentId: string }>();
    return <Navigate to={`/ai-employees/${agentId ?? ''}`} replace />;
}

/**
 * Sentry's wrapper around createBrowserRouter. It is what lets navigation
 * spans be named by ROUTE PATTERN (`/campaigns/:id`) instead of by concrete
 * URL — without it every campaign detail page becomes its own transaction and
 * the performance view is thousands of one-hit rows instead of one aggregate.
 * Behaviourally identical to createBrowserRouter for routing purposes.
 */
const createRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = createRouter([
    {
        element: <PublicRoute />,
        children: [
            {
                element: <AuthLayout />,
                children: [
                    {
                        path: PUBLIC_ROUTE.LOGIN,
                        element: <LoginPage />,
                    },
                    {
                        path: PUBLIC_ROUTE.REGISTER,
                        element: <RegisterPage />,
                    },
                    {
                        path: PUBLIC_ROUTE.SIGNUP,
                        element: <RegisterPage />,
                    },
                    {
                        path: PUBLIC_ROUTE.FORGOT_PASSWORD,
                        element: <ForgotPasswordPage />,
                    },
                    {
                        path: PUBLIC_ROUTE.RESET_PASSWORD,
                        element: <ResetPasswordPage />,
                    },
                ],
            },
        ],
    },
    {
        element: <ProtectedRoute />,
        children: [
            {
                // Deliberately OUTSIDE <AppLayout>: the form preview must show
                // the page exactly as a visitor sees it, so it gets no sidebar
                // and no app header — only its own preview bar.
                path: PRIVATE_ROUTES.FORM_PREVIEW,
                element: <LeadFormPreviewPage />,
            },
            {
                element: <AppLayout />,
                children: [
                    {
                        // Today is the landing route. `HOME` stays `'/'` so
                        // everything that navigates "home" after finishing
                        // something keeps working without knowing which page
                        // home currently is.
                        path: PRIVATE_ROUTES.HOME,
                        element: <HomeRedirect />,
                    },
                    {
                        path: PRIVATE_ROUTES.TODAY,
                        element: <TodayPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.TODAY_LEADS,
                        element: <TodayLeadsPage />,
                    },
                    {
                        // Legacy home — Today became Daily Updates (ticket
                        // [13] rename). Splat + query + hash must survive;
                        // bookmarks and pre-rename "Open Today" links point
                        // here.
                        path: '/today/*',
                        element: <LegacyRedirect toBase="/daily-updates" />,
                    },
                    {
                        path: PRIVATE_ROUTES.OUTCOMES,
                        element: <OutcomesPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.OUTCOMES_LEADS,
                        element: <OutcomesLeadsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.DASHBOARD,
                        element: <DashboardPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.DASHBOARD_DETAIL,
                        element: <DashboardPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.GETTING_STARTED,
                        element: <GettingStartedRoute />,
                    },
                    {
                        path: PRIVATE_ROUTES.AI_SETUP,
                        element: <AiSetupPage />,
                    },
                    // Agency console — withdrawn from the UI behind
                    // `IS_AGENCY_MODULE_ENABLED`. Unregistered rather than
                    // merely hidden from the nav, because `/agency` renders a
                    // "create your agency" form for a user without one, so a
                    // typed URL would otherwise still let anyone self-onboard.
                    // These now fall through to the `*` NotFound route.
                    ...(IS_AGENCY_MODULE_ENABLED
                        ? [
                              {
                                  path: PRIVATE_ROUTES.AGENCY,
                                  element: <AgencyOverviewPage />,
                              },
                              {
                                  path: PRIVATE_ROUTES.AGENCY_CLIENTS,
                                  element: <AgencyClientsPage />,
                              },
                              {
                                  path: PRIVATE_ROUTES.AGENCY_CLIENT_NEW,
                                  element: <AddClientWizardPage />,
                              },
                              {
                                  path: PRIVATE_ROUTES.AGENCY_PRICING,
                                  element: <AgencyPricingPage />,
                              },
                              {
                                  path: PRIVATE_ROUTES.AGENCY_SETUPS,
                                  element: <AgencySetupsPage />,
                              },
                              {
                                  path: PRIVATE_ROUTES.AGENCY_BRANDING,
                                  element: <AgencyBrandingPage />,
                              },
                          ]
                        : []),
                    {
                        path: PRIVATE_ROUTES.ANALYTICS,
                        element: <Navigate to={PRIVATE_ROUTES.ANALYTICS_OVERVIEW} replace />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_OVERVIEW,
                        element: <AnalyticsOverviewPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_MARKETING,
                        element: <AnalyticsMarketingPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_CONVERSATIONS,
                        element: <AnalyticsConversationsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_REVENUE,
                        element: <AnalyticsRevenuePage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_APPOINTMENTS,
                        element: <AnalyticsAppointmentsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ANALYTICS_DELIVERABILITY,
                        element: <AnalyticsDeliverabilityPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CAMPAIGNS,
                        element: <CampaignsPage />,
                    },
                    {
                        path: 'campaigns/new',
                        element: <CreateCampaignPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CAMPAIGN_DESIGN,
                        element: <CampaignDesignPage />,
                    },
                    // Ordered ABOVE `campaigns/:id` so the static `/edit` segment is
                    // matched first — the same reason `/settings/channels/whatsapp`
                    // sits above `:channelType`.
                    {
                        path: 'campaigns/:id/edit',
                        element: <EditCampaignPage />,
                    },
                    {
                        path: 'campaigns/:id',
                        element: <CampaignDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.IMPORT_CONTACTS,
                        element: <ImportContactsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CONTACTS,
                        element: <ContactsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CONTACT_DETAIL,
                        element: <ContactDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SEGMENTS,
                        element: <SegmentsPage />,
                    },
                    {
                        // Declared BEFORE SEGMENT_DETAIL: `/segments/:segmentId`
                        // matches the literal "new" too, so the detail page would
                        // win and try to load a segment with that id. Same trap the
                        // backend notes on its own `POST /segments/preview` route.
                        path: PRIVATE_ROUTES.SEGMENT_NEW,
                        element: <SegmentCreatePage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SEGMENT_DETAIL,
                        element: <SegmentDetailPage />,
                    },
                    {
                        path: 'settings',
                        element: <SettingsLayout />,
                        children: [
                            {
                                path: 'organization',
                                element: <OrganizationSettingsPage />,
                            },
                            {
                                path: 'channels',
                                element: <SettingsChannelsPage />,
                            },
                            {
                                path: 'channels/:channelType/:provider',
                                element: <ChannelProviderPage />,
                            },
                            {
                                // WhatsApp left the generic channel shell for its
                                // own settings page. React Router scores this
                                // static segment above the `:channelType` pattern
                                // below, so old deep links land on the redirect
                                // rather than the SMS-shaped detail page.
                                path: 'channels/whatsapp',
                                element: <Navigate to={PRIVATE_ROUTES.SETTINGS_WHATSAPP} replace />,
                            },
                            {
                                path: 'channels/:channelType',
                                element: <ChannelDetailPage />,
                            },
                            {
                                // Legacy path — redirects to /settings/templates/sms,
                                // preserving the query string (text-link wizard
                                // deep-link: ?templateId=new&returnTo=…).
                                path: 'sms-templates',
                                element: <SmsTemplatesRedirect />,
                            },
                            {
                                // Legacy home — templates moved to /templates
                                // (2026-07 IA redesign). Splat + query + hash
                                // must survive; the email-template builder's
                                // returnTo round-trip points here from old
                                // sessions.
                                path: 'templates/*',
                                element: <LegacyRedirect toBase="/templates" />,
                            },
                            {
                                path: 'email/domains',
                                element: <EmailDomainsPage />,
                            },
                            {
                                path: 'email/senders',
                                element: <EmailSendersPage />,
                            },
                            {
                                path: 'whatsapp',
                                element: <WhatsAppSettingsPage />,
                            },
                            {
                                path: 'whatsapp/connect',
                                element: <WhatsAppConnectPage />,
                            },
                            {
                                path: 'module-setup',
                                element: <ModuleSetupPage />,
                            },
                            {
                                path: 'api-keys',
                                element: <ApiKeysSettingsPage />,
                            },
                            {
                                path: 'webhooks',
                                element: <WebhooksSettingsPage />,
                            },
                            {
                                path: 'webhooks/new',
                                element: <WebhookFormPage />,
                            },
                            {
                                path: 'webhooks/:id/edit',
                                element: <WebhookFormPage />,
                            },
                            {
                                // Legacy home — tags moved to /tags.
                                path: 'tags',
                                element: <LegacyRedirect toBase="/tags" />,
                            },
                            {
                                path: 'calendars',
                                element: <CalendarsSettingsPage />,
                            },
                            {
                                path: 'field-service',
                                element: <FieldServiceSettingsPageRoute />,
                            },
                            {
                                // Sample data was removed; keep old links from 404ing.
                                path: 'sample-data',
                                element: (
                                    <Navigate to={PRIVATE_ROUTES.SETTINGS_ORGANIZATION} replace />
                                ),
                            },
                            {
                                // Legacy home — the phone lives on the employee's Phone tab.
                                path: 'ai-phone',
                                element: <AiPhoneRedirect />,
                            },
                            {
                                // Legacy home — AI calls are in the Calls history.
                                path: 'ai-phone/calls',
                                element: <AiPhoneCallsRedirect />,
                            },
                            {
                                path: 'opt-out',
                                element: <OptOutSettingsPage />,
                            },
                            {
                                path: 'chat-widget',
                                element: <ChatWidgetSettingsPage />,
                            },
                            {
                                path: 'users',
                                element: <UsersPage />,
                            },
                            {
                                path: 'roles',
                                element: <RolesPage />,
                            },
                            {
                                path: 'voice',
                                element: <VoiceSettingsPage />,
                            },
                            {
                                path: 'voice/:channelIdentityId',
                                element: <VoiceNumberSettingsPage />,
                            },
                        ],
                    },
                    {
                        path: PRIVATE_ROUTES.TEMPLATES,
                        element: <TemplatesPage />,
                        children: [
                            {
                                // Permission-aware: routes to the first tab the
                                // user can actually read (email-only users must
                                // not land on the SMS tab).
                                index: true,
                                element: <TemplatesIndexRedirect />,
                            },
                            {
                                path: 'sms',
                                element: <TemplatesSmsTab />,
                            },
                            {
                                path: 'email',
                                element: <TemplatesEmailTab />,
                            },
                            {
                                path: 'whatsapp',
                                element: <TemplatesWhatsAppTab />,
                            },
                            {
                                path: 'library',
                                element: <TemplatesLibraryTab />,
                            },
                        ],
                    },
                    {
                        // Outside the Templates shell on purpose: the editor is
                        // a full-height page with its own action bar, not a tab.
                        path: PRIVATE_ROUTES.TEMPLATES_WHATSAPP_EDITOR,
                        element: <WhatsAppTemplateEditorPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.TAGS,
                        element: <TagsSettingsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CHANNELS,
                        element: <ChannelsHubPage />,
                    },
                    {
                        path: 'channels/:channelType',
                        element: <ChannelRedirect />,
                    },
                    {
                        path: PRIVATE_ROUTES.INBOX,
                        element: <InboxPage />,
                    },
                    {
                        // Deep-link to a specific unified conversation. Same
                        // page — the underlying component reads
                        // `:unifiedConversationId` itself.
                        path: PRIVATE_ROUTES.INBOX_DETAIL,
                        element: <InboxPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.JOIN_LINKS,
                        element: <JoinLinksPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.CREATE_JOIN_LINK,
                        element: <JoinLinkFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.EDIT_JOIN_LINK,
                        element: <JoinLinkFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.JOIN_LINK_DETAIL,
                        element: <JoinLinkDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SHORT_LINK_DETAIL,
                        element: <ShortLinkDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.FORMS,
                        element: <LeadFormsPage />,
                    },
                    {
                        // Must precede FORM_DETAIL so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.FORM_NEW,
                        element: <LeadFormWizardPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.FORM_TEMPLATES,
                        element: <LeadFormTemplatesPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.FORM_EDIT,
                        element: <LeadFormWizardPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.FORM_DETAIL,
                        element: <LeadFormDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.JOIN_KEYWORDS,
                        element: <JoinKeywordsPage />,
                    },
                    {
                        // Must precede JOIN_KEYWORD_DETAIL so "new" is not captured as an id
                        // (same precedent as SEQUENCE_NEW / JOIN_LINKS below).
                        path: PRIVATE_ROUTES.CREATE_JOIN_KEYWORD,
                        element: <JoinKeywordFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.EDIT_JOIN_KEYWORD,
                        element: <JoinKeywordFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.JOIN_KEYWORD_DETAIL,
                        element: <JoinKeywordDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.POLLS,
                        element: <PollsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.POLL_CREATE,
                        element: <CreatePollPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.POLL_ANALYTICS,
                        element: <PollAnalyticsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.POLL_DETAIL,
                        element: <PollDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.KNOWLEDGE_BASES,
                        element: <KnowledgeBasesPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.KNOWLEDGE_BASE_DETAIL,
                        element: <KnowledgeBaseDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.AI_LEAD_RESPONDER,
                        element: <AiLeadResponderPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.AI_EMPLOYEES,
                        element: <EmployeesListPage />,
                    },
                    {
                        // Must precede the `:employeeId` route so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.AI_EMPLOYEE_NEW,
                        element: <EmployeeWizardPage />,
                    },
                    {
                        // `EmployeeProfilePage` renders the wizard itself
                        // (`EmployeeWizardPage`) while the employee is still DRAFT —
                        // this is where a draft resumes at `?step=N` after the
                        // Details step creates it — and the tabbed profile view
                        // (Overview / Activity / Phone / Advanced) otherwise.
                        path: PRIVATE_ROUTES.AI_EMPLOYEE_DETAIL,
                        element: <EmployeeProfilePage />,
                    },
                    // Legacy `/ai-agents` paths — redirect sources only (§ nav rename).
                    // The old agent-builder feature module was removed entirely in Task 18.
                    {
                        path: PRIVATE_ROUTES.AI_AGENTS,
                        element: <Navigate to={PRIVATE_ROUTES.AI_EMPLOYEES} replace />,
                    },
                    {
                        // Must precede the `:agentId` redirect so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.AI_AGENT_NEW,
                        element: <Navigate to={PRIVATE_ROUTES.AI_EMPLOYEE_NEW} replace />,
                    },
                    {
                        path: PRIVATE_ROUTES.AI_AGENT_DETAIL,
                        element: <AiAgentDetailRedirect />,
                    },
                    {
                        path: PRIVATE_ROUTES.CALENDAR,
                        element: <CalendarPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.MEETING_TYPES,
                        element: <MeetingTypesPage />,
                    },
                    {
                        // Must precede the `:id/edit` route so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.MEETING_TYPE_NEW,
                        element: <MeetingTypeFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.MEETING_TYPE_EDIT,
                        element: <MeetingTypeFormPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ROUTING_FORMS,
                        element: <RoutingFormsPage />,
                    },
                    {
                        // Must precede the `:id/edit` route so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.ROUTING_FORM_NEW,
                        element: <RoutingFormBuilderPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ROUTING_FORM_EDIT,
                        element: <RoutingFormBuilderPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.APPOINTMENT_OUTCOMES,
                        element: <AppointmentOutcomesPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.AVAILABILITY,
                        element: <AvailabilityPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.BOOKINGS,
                        element: <BookingsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.AUTOMATIONS,
                        element: <AutomationsPage />,
                    },
                    {
                        // Moved, not removed: every old link lands on the
                        // owner's own automations with its query intact.
                        path: PRIVATE_ROUTES.WORKFLOWS,
                        element: (
                            <QueryPreservingRedirect
                                to={PRIVATE_ROUTES.AUTOMATIONS}
                                add={{ view: 'mine' }}
                                transform={normaliseLegacyWorkflowStatus}
                            />
                        ),
                    },
                    {
                        // Must precede the `:id` routes so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.WORKFLOW_NEW,
                        element: <NewAutomationPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.WORKFLOW_BUILDER,
                        element: <WorkflowBuilderPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.WORKFLOW_RUNS,
                        element: <WorkflowRunsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.WORKFLOW_ANALYTICS,
                        element: <WorkflowAnalyticsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.BILLING,
                        element: <BillingPage />,
                    },
                    // Old billing sub-pages folded into the tabbed page —
                    // preserve deep links (bookmarks, emails) via redirects.
                    {
                        path: '/billing/usage',
                        element: <Navigate to="/billing?tab=usage" replace />,
                    },
                    {
                        path: '/billing/credits',
                        element: <Navigate to="/billing?tab=usage" replace />,
                    },
                    {
                        path: '/billing/add-ons',
                        element: <Navigate to="/billing?tab=subscription" replace />,
                    },
                    {
                        path: PRIVATE_ROUTES.MODULE_RECORDS,
                        element: <ModuleRecordsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.MODULE_RECORD_DETAIL,
                        element: <ModuleRecordDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.TICKETS,
                        element: <TicketsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.TICKET_DETAIL,
                        element: <TicketDetailPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.OPPORTUNITIES,
                        element: <OpportunitiesPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.OPPORTUNITY_REPORTS,
                        element: <OpportunityReportsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.OPPORTUNITY_DETAIL,
                        element: <OpportunityDetailPage />,
                    },
                    {
                        // The Sales stages page was removed; stage setup lives in the
                        // board's Customize pipeline sheet. Old links land on the board.
                        path: PRIVATE_ROUTES.PIPELINES,
                        element: <Navigate to={PRIVATE_ROUTES.OPPORTUNITIES} replace />,
                    },
                    {
                        path: PRIVATE_ROUTES.CALLS,
                        element: <CallsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING,
                        element: <Navigate to={PRIVATE_ROUTES.ADVERTISING_PERFORMANCE} replace />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING_GOOGLE,
                        element: <AdvertisingGooglePage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING_META,
                        element: <AdvertisingMetaPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING_ACCOUNTS,
                        element: <AdvertisingConnectedAccountsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING_CONNECT,
                        element: <AdvertisingConnectAccountPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.ADVERTISING_PERFORMANCE,
                        element: <AdvertisingCampaignPerformancePage />,
                    },
                    {
                        // Lead sources now live on the Performance page; keep old
                        // bookmarks (and their date range) working.
                        path: PRIVATE_ROUTES.ADVERTISING_LEAD_SOURCES,
                        element: (
                            <QueryPreservingRedirect to={PRIVATE_ROUTES.ADVERTISING_PERFORMANCE} />
                        ),
                    },
                    {
                        // Sequences are automations with a simpler editor, so
                        // the old list narrows the one list instead.
                        path: PRIVATE_ROUTES.SEQUENCES,
                        element: (
                            <QueryPreservingRedirect
                                to={PRIVATE_ROUTES.AUTOMATIONS}
                                add={{ view: 'mine', kind: 'SEQUENCE' }}
                            />
                        ),
                    },
                    {
                        // Must precede the `:id` routes so "new" is not captured as an id.
                        path: PRIVATE_ROUTES.SEQUENCE_NEW,
                        element: <SequenceWizardPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SEQUENCE_EDITOR,
                        element: <SequenceEditorPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SEQUENCE_DESIGN,
                        element: <SequenceDesignPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.SEQUENCE_ANALYTICS,
                        element: <SequenceAnalyticsPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.EMAIL_TEMPLATE_BUILDER,
                        element: <EmailTemplateBuilderPage />,
                    },
                    {
                        path: PRIVATE_ROUTES.PROFILE,
                        element: <ProfilePage />,
                    },
                ],
            },
        ],
    },
    {
        path: PUBLIC_ROUTE.JOIN,
        element: <PublicJoinPageRoute />,
    },
    {
        path: PUBLIC_ROUTE.FORM,
        element: <PublicLeadFormPageRoute />,
    },
    {
        // Registered before BOOK so "manage" is never captured as an org slug.
        path: PUBLIC_ROUTE.BOOK_MANAGE,
        element: <ManageBookingPageRoute />,
    },
    {
        path: PUBLIC_ROUTE.BOOK_ROUTING,
        element: <PublicRoutingFormPageRoute />,
    },
    {
        path: PUBLIC_ROUTE.BOOK,
        element: <PublicBookingPageRoute />,
    },
    {
        path: PUBLIC_ROUTE.ACCEPT_INVITATION,
        element: <AcceptInvitationPage />,
    },
    {
        // Outside PublicRoute on purpose: a user who already holds a token (a
        // second OAuth round-trip, a stale session in another tab) would
        // otherwise be bounced to the dashboard before the ticket in the URL
        // fragment could be exchanged. Keeps the auth shell for the interim card.
        element: <AuthLayout />,
        children: [
            {
                path: PUBLIC_ROUTE.OAUTH_CALLBACK,
                element: <OAuthCallbackPage />,
            },
        ],
    },
    {
        // Outside both guards on purpose: reached with a token (just removed
        // from the org) or without one (declined an invitation while signed out).
        path: PUBLIC_ROUTE.NO_WORKSPACE,
        element: <NoWorkspacePage />,
    },
    {
        path: PUBLIC_ROUTE.LINK_EXPIRED,
        element: <LinkExpiredPage />,
    },
    {
        path: PUBLIC_ROUTE.SHORT_LINK_REDIRECT,
        element: <ShortLinkRedirectPage />,
    },
    {
        path: PUBLIC_ROUTE.SHORT_LINK_REDIRECT_TOKEN,
        element: <ShortLinkRedirectPage />,
    },
    {
        path: '/design-system',
        element: <DesignSystemPage />,
    },
    {
        path: '*',
        element: <NotFoundPage />,
    },
]);
